import { useEffect, useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { z } from 'zod';
import Drawer from '@/components/Modals/Drawer';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { IDocument } from '@/types';
import axiosClient from '@/utils/axiosClient';

const formSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  body: z.string().min(1, { message: 'Content is required' }),
});

function mapValues(document: IDocument) {
  return {
    name: document.name || '',
    description: document.description || '',
    body: document.body || '',
  };
}
interface DrawerProps {
  document: IDocument;
}

const DocumentDrawer = NiceModal.create(({ document }: DrawerProps) => {
  const queryClient = useQueryClient();

  const modal = useModal('document-drawer');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      body: '',
    },
  });

  useEffect(() => {
    if (document) {
      const formValues = mapValues(document);
      form.reset(formValues as any);
    }
  }, [document]);

  const { handleSubmit } = form;

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      if (document) {
        await axiosClient.put(`v1/documents/${document.id}`, data);
      } else {
        await axiosClient.post('v1/documents', data);
      }

      queryClient.invalidateQueries('v1/documents');
      modal.hide();
    } catch (error) {
      console.error('Error saving document:', error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Drawer modal={modal} title="Documents">
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="flex w-full gap-8 my-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <div className="w-full">
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Name" autoComplete="off" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea placeholder="Description of the document" className="min-h-24" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="body"
            render={({ field }) => (
              <FormItem className="mt-4">
                <FormLabel>Contents</FormLabel>
                <FormControl>
                  <Textarea placeholder="Contents of the document" className="min-h-72" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={isSubmitting} className="mt-8">
            <Save />
            {isSubmitting ? 'Saving...' : 'Save Document'}
          </Button>
        </form>
      </Form>
    </Drawer>
  );
});

export default DocumentDrawer;
